import React from 'react';
import { Box, Stack, IconButton, Typography, Button, TextField, alpha, useTheme, Grid } from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, CalendarMonth as CalendarMonthIcon } from '@mui/icons-material';
import FormInput from '../../inputs/FormInput';

const RoomAssignmentCard = ({
    assignment,
    hotelId,
    extraOptions,
    extraOptionsForSelect,
    roomCategoriesOptions,
    creatingCategory,
    isArabic,
    onUpdateRoomStay,
    onUpdateRoomStayExtra,
    onRemoveRoomStayExtra,
    onRemoveRoomAssignment,
    onAddExtraToAllStays,
    onCreateCategory,
}) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                mb: 1.5, p: 1.5, borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                bgcolor: alpha(theme.palette.background.paper, 0.4),
            }}
        >
            {/* Assignment header: date range + delete */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <CalendarMonthIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.75rem' }}>
                    {assignment.from_date} → {assignment.to_date}  ({assignment.nights} {isArabic ? 'ليالي' : 'nights'})
                </Typography>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" color="error" onClick={() => onRemoveRoomAssignment(hotelId, assignment)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Stack>

            {/* Assignment fields (Restructured to 2 rows of equal sizes) */}
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid item xs={6}>
                    <FormInput
                        label={isArabic ? 'فئة الغرفة' : 'Room Category'}
                        type="select"
                        name={`ass_cat_${hotelId}_${assignment.id}`}
                        value={assignment.room_category_id || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'room_category_id', val));
                        }}
                        options={roomCategoriesOptions}
                        creatable
                        disabled={creatingCategory !== null}
                        loading={creatingCategory === `${hotelId}_${assignment.id}`}
                        onCreate={(inputValue) => onCreateCategory(hotelId, assignment.id, inputValue)}
                        size="small"
                        sx={{ width: '100%' }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormInput
                        label={isArabic ? 'العدد' : 'Rooms'}
                        type="number"
                        name={`ass_count_${hotelId}_${assignment.id}`}
                        value={assignment.rooms_count}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'rooms_count', val));
                        }}
                        inputProps={{ min: 1 }}
                        size="small"
                        sx={{ width: '100%' }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }} flexWrap="wrap">
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {isArabic ? 'سعر البيع' : 'Sell Price'}
                        </Typography>
                        <Box
                            sx={{
                                px: 0.6, py: 0.15, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                fontSize: '0.6rem', fontWeight: 600, color: 'success.main',
                                lineHeight: 1.4, whiteSpace: 'nowrap',
                            }}
                        >
                            {(parseFloat(assignment.custom_sell_price) || 0)} {isArabic ? '×' : '×'} {assignment.nights} {isArabic ? 'ليلة' : 'nights'}
                        </Box>
                        <Box
                            sx={{
                                px: 0.6, py: 0.15, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                fontSize: '0.65rem', fontWeight: 700, color: 'success.dark',
                                lineHeight: 1.4,
                            }}
                        >
                            = {(parseFloat(assignment.custom_sell_price) || 0) * assignment.nights}
                        </Box>
                    </Stack>
                    <TextField
                        type="number"
                        size="small"
                        value={assignment.custom_sell_price ?? ''}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'custom_sell_price', val));
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }} flexWrap="wrap">
                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {isArabic ? 'التكلفة' : 'Cost Price'}
                        </Typography>
                        <Box
                            sx={{
                                px: 0.6, py: 0.15, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                fontSize: '0.6rem', fontWeight: 600, color: 'info.main',
                                lineHeight: 1.4, whiteSpace: 'nowrap',
                            }}
                        >
                            {(parseFloat(assignment.custom_cost_price) || 0)} {isArabic ? '×' : '×'} {assignment.nights} {isArabic ? 'ليلة' : 'nights'}
                        </Box>
                        <Box
                            sx={{
                                px: 0.6, py: 0.15, borderRadius: '4px',
                                bgcolor: alpha(theme.palette.info.main, 0.12),
                                fontSize: '0.65rem', fontWeight: 700, color: 'info.dark',
                                lineHeight: 1.4,
                            }}
                        >
                            = {(parseFloat(assignment.custom_cost_price) || 0) * assignment.nights}
                        </Box>
                    </Stack>
                    <TextField
                        type="number"
                        size="small"
                        value={assignment.custom_cost_price ?? ''}
                        onChange={(e) => {
                            const val = e.target.value === '' ? '' : e.target.value;
                            assignment.stays.forEach(rs => onUpdateRoomStay(hotelId, rs.id, 'custom_cost_price', val));
                        }}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' } }}
                    />
                </Grid>
            </Grid>

            {/* Assignment extras */}
            <Box sx={{ pl: 1 }}>
                {(assignment.extras || []).map((ex, exIdx) => (
                    <Box
                        key={ex.id}
                        sx={{
                            p: 1, mb: 1, borderRadius: 1,
                            bgcolor: alpha(theme.palette.primary.main, 0.04),
                            border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
                        }}
                    >
                        <Grid container spacing={1.5} alignItems="center">
                            <Grid item xs={8}>
                                <FormInput
                                    label={isArabic ? 'الإضافة' : 'Extra'}
                                    type="select"
                                    name={`ass_ext_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.hotel_extra_id || ''}
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const found = extraOptions?.find(o => String(o.value ?? o.id) === String(selectedId));
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) {
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'hotel_extra_id', selectedId);
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'name', found?.label || found?.name || '');
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'pricing_basis', found?.default_pricing_basis || 'once');
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_sell_price', parseFloat(found?.default_sell_price) || 0);
                                                onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_cost_price', parseFloat(found?.default_cost_price) || 0);
                                            }
                                        });
                                    }}
                                    options={extraOptionsForSelect}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormInput
                                    label={isArabic ? 'الكمية' : 'Qty'}
                                    type="number"
                                    name={`ass_ext_qty_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.quantity}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'quantity', val);
                                        });
                                    }}
                                    inputProps={{ min: 1 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <FormInput
                                    label={isArabic ? 'سعر البيع' : 'Sell'}
                                    type="number"
                                    name={`ass_ext_sell_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.custom_sell_price}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_sell_price', val);
                                        });
                                    }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <FormInput
                                    label={isArabic ? 'التكلفة' : 'Cost'}
                                    type="number"
                                    name={`ass_ext_cost_${hotelId}_${assignment.id}_${exIdx}`}
                                    value={ex.custom_cost_price}
                                    onChange={(e) => {
                                        const val = e.target.value === '' ? '' : e.target.value;
                                        assignment.stays.forEach(rs => {
                                            const exOnRs = (rs.extras || [])[exIdx];
                                            if (exOnRs) onUpdateRoomStayExtra(hotelId, rs.id, exOnRs.id, 'custom_cost_price', val);
                                        });
                                    }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    size="small"
                                    sx={{ width: '100%' }}
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center', pt: 2.5 }}>
                                <IconButton size="small" color="error" onClick={() => {
                                    assignment.stays.forEach(rs => {
                                        const exOnRs = (rs.extras || [])[exIdx];
                                        if (exOnRs) onRemoveRoomStayExtra(hotelId, rs.id, exOnRs.id);
                                    });
                                }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                <Button
                    size="small"
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={() => onAddExtraToAllStays(hotelId, assignment)}
                    sx={{ fontSize: '0.7rem', py: 0.25 }}
                >
                    {isArabic ? 'إضافة خدمة' : 'Add Extra'}
                </Button>
            </Box>
        </Box>
    );
};

export default RoomAssignmentCard;
